import React, { useState } from 'react';
import { 
  ShieldAlert, 
  Lock, 
  FileText
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

export const Footer = () => {
  const [openDisclaimer, setOpenDisclaimer] = useState(false);
  const [openPrivacy, setOpenPrivacy] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);

  return (
    <footer className="fixed bottom-0 left-0 right-0 border-t bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-40 py-4 h-16">
      <div className="container flex items-center justify-between text-base">
        <div className="flex items-center gap-3">
          <span>© {new Date().getFullYear()} docverifai.com</span>
          <span className="text-muted-foreground/40">|</span>
          
          {/* Privacy Policy Dialog */}
          <Dialog open={openPrivacy} onOpenChange={setOpenPrivacy}>
            <DialogTrigger asChild>
              <Button variant="link" className="px-0 text-muted-foreground hover:text-primary h-auto">
                Privacy Policy
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto p-6">
              <div className="flex items-center mb-6">
                <Lock className="h-6 w-6 text-primary mr-2" />
                <h2 className="text-2xl font-bold">Privacy Policy</h2>
              </div>
              <Separator className="mb-6" />
              
              <p className="text-muted-foreground mb-6">Last updated: 4/6/2025</p>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold">1. Introduction</h3>
                  <p className="mt-2">
                    DocVerifAI ("we", "our", or "us") is <span className="font-semibold">committed to protecting your privacy</span>. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our document verification service.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold">2. Information We Collect</h3>
                  <p className="mt-2">We collect information that you provide directly to us, including:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><span className="font-semibold">Account information</span> (name, email address)</li>
                    <li><span className="font-semibold">Documents</span> you upload for verification</li>
                    <li><span className="font-semibold">Organization information</span></li>
                    <li><span className="font-semibold">Usage data</span> and analytics</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold">3. How We Use Your Information</h3>
                  <p className="mt-2">We use the information we collect to:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Provide, maintain, and improve our services</li>
                    <li>Process and analyze documents</li>
                    <li>Communicate with you about our services</li>
                    <li>Protect against fraud and unauthorized access</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold">4. Data Storage and Security</h3>
                  <p className="mt-2">
                    We use <span className="font-semibold">industry-standard security measures</span> to protect your information. Your documents and data are stored securely using encryption and access controls.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold">5. Third-Party Services</h3>
                  <p className="mt-2">
                    We use trusted third-party services for:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Authentication (Clerk)</li>
                    <li>Data storage (Supabase)</li>
                    <li>Document processing</li>
                  </ul>
                  <p className="mt-2">
                    These services have their own privacy policies and security measures.
                  </p>
                </div>
                
                <div className="p-4 border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 rounded-md">
                  <h3 className="text-xl font-semibold">6. Your Rights</h3>
                  <p className="mt-2">You have the right to:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><span className="font-semibold">Access</span> your personal information</li>
                    <li><span className="font-semibold">Correct</span> inaccurate information</li>
                    <li><span className="font-semibold">Request deletion</span> of your information</li>
                    <li><span className="font-semibold">Opt-out</span> of marketing communications</li>
                  </ul>
                  <p className="mt-2 text-sm">
                    <span className="font-semibold text-amber-600">Important:</span> To exercise these rights, please contact us using the information in Section 7.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold">7. Contact Us</h3>
                  <p className="mt-2">
                    If you have any questions about this Privacy Policy, please contact us at:{' '}
                    <a href="mailto:hello@docverifai.com" className="text-primary hover:underline font-semibold">
                      hello@docverifai.com
                    </a>
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          <span className="text-muted-foreground/40">|</span>
          
          {/* Terms of Service Dialog */}
          <Dialog open={openTerms} onOpenChange={setOpenTerms}>
            <DialogTrigger asChild>
              <Button variant="link" className="px-0 text-muted-foreground hover:text-primary h-auto">
                Terms of Service
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto p-6">
              <div className="flex items-center mb-6">
                <FileText className="h-6 w-6 text-primary mr-2" />
                <h2 className="text-2xl font-bold">Terms of Service</h2>
              </div>
              <Separator className="mb-6" />
              
              <p className="text-muted-foreground mb-6">Last updated: 4/6/2025</p>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold">1. Acceptance of Terms</h3>
                  <p className="mt-2">
                    By accessing and using DocVerifAI ("Service"), you <span className="font-semibold">accept and agree to be bound</span> by these Terms of Service. If you do not agree to these terms, <span className="font-semibold text-amber-600">do not use the Service</span>.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold">2. Description of Service</h3>
                  <p className="mt-2">
                    DocVerifAI provides document verification and analysis services. We process documents to extract and verify information using artificial intelligence and machine learning technologies.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold">3. User Responsibilities</h3>
                  <p className="mt-2">You agree to:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Provide <span className="font-semibold">accurate information</span> when using the Service</li>
                    <li>Maintain the <span className="font-semibold">security of your account credentials</span></li>
                    <li>Use the Service in <span className="font-semibold">compliance with applicable laws</span></li>
                    <li><span className="font-semibold">Not misuse</span> or attempt to circumvent the Service</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold">4. Intellectual Property</h3>
                  <p className="mt-2">
                    The Service, including its original content, features, and functionality, is <span className="font-semibold">owned by DocVerifAI</span> and is protected by international copyright, trademark, and other intellectual property laws.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold">5. Privacy and Data Protection</h3>
                  <p className="mt-2">
                    Your use of the Service is also governed by our <span className="font-semibold">Privacy Policy</span>. By using the Service, you consent to the collection and use of information as detailed in our Privacy Policy.
                  </p>
                </div>
                
                <div className="p-4 border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 rounded-md">
                  <h3 className="text-xl font-semibold">6. Limitation of Liability</h3>
                  <p className="mt-2">
                    DocVerifAI <span className="font-semibold text-amber-600">shall not be liable</span> for any:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Indirect damages</li>
                    <li>Incidental damages</li>
                    <li>Special damages</li>
                    <li>Consequential damages</li>
                    <li>Punitive damages</li>
                  </ul>
                  <p className="mt-2">
                    resulting from your use of or inability to use the Service.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold">7. Changes to Terms</h3>
                  <p className="mt-2">
                    We reserve the right to <span className="font-semibold">modify these terms at any time</span>. We will notify users of any material changes via email or through the Service.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold">8. Contact Information</h3>
                  <p className="mt-2">
                    For questions about these Terms of Service, please contact us at:{' '}
                    <a href="mailto:hello@docverifai.com" className="text-primary hover:underline font-semibold">
                      hello@docverifai.com
                    </a>
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Disclaimer Dialog */}
          <Dialog open={openDisclaimer} onOpenChange={setOpenDisclaimer}>
            <DialogTrigger asChild>
              <Button variant="link" className="px-0 text-muted-foreground hover:text-primary h-auto flex items-center gap-1">
                <span>Disclaimer: For guidance only. Not definitive or legal advice.</span>
                <ShieldAlert className="h-5 w-5" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl p-6">
              <div className="flex items-center mb-6">
                <ShieldAlert className="h-6 w-6 text-primary mr-2" />
                <h2 className="text-2xl font-bold">Disclaimer</h2>
              </div>
              <Separator className="mb-6" />
              
              <p className="text-muted-foreground mb-6">Last updated: 4/6/2025</p>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold">1. Purpose</h3>
                  <p className="mt-2">
                    This platform is designed to assist with compliance screening by identifying 
                    entities that may be subject to sanctions, watchlists, or other risk indicators. 
                    The information provided is <span className="font-semibold">for general informational purposes only</span> and 
                    <span className="font-semibold text-amber-600"> does not constitute legal, regulatory, or financial advice</span>.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold">2. Limitations</h3>
                  <p className="mt-2">
                    While we strive to ensure the accuracy and timeliness of the data, we make <span className="font-semibold">no 
                    representations or warranties</span> of any kind, express or implied, about:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>The completeness of the information</li>
                    <li>The reliability of the data</li>
                    <li>The suitability of the information for any particular purpose</li>
                    <li>The accuracy of any analysis or classification</li>
                  </ul>
                  <p className="mt-2">
                    Users are <span className="font-semibold">responsible for conducting their own due diligence</span> and for consulting qualified 
                    professionals before making decisions based on the platform's outputs.
                  </p>
                </div>
                
                <div className="p-4 border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 rounded-md">
                  <h3 className="text-xl font-semibold">3. Additional Protections</h3>
                  <p className="mt-2">
                    By using this platform, you acknowledge and agree that:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>You bear <span className="font-semibold">sole responsibility</span> for any decisions made based on the information provided</li>
                    <li>You <span className="font-semibold">agree to indemnify and hold harmless</span> DocVerifAI and its affiliates from any claims, damages, or liabilities arising from your use of the platform</li>
                    <li>The platform should be used as a <span className="font-semibold">supplementary tool only</span>, not as the sole basis for decision-making</li>
                    <li>For complete details regarding our legal relationship, please refer to our Terms of Service</li>
                  </ul>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </footer>
  );
}; 
